// src/lib/profile-inventory.ts
// Builds an evidence library from user profile data

import { supabase } from './supabase';
import {
  EvidenceItem,
  EvidenceSourceType,
  EvidenceMetadata,
  EvidenceLibrary,
  ContractScoredLibrary,
  ContractForMatching,
  ScoredEvidenceItem
} from '../types/source-grounded';

import { CapabilityInferencer, inferCapabilitiesFromArray } from './capability-inferencer';
import { RelevanceScorer, groupByTier } from './relevance-scorer';

/**
 * Safely convert a value to an array (handles string, array, null, undefined)
 */
function toArray(value: any): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    // Handle comma-separated or newline-separated strings
    return value.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
  }
  return [];
}

/**
 * Counter for generating evidence IDs
 */
const evidenceCounters: Record<string, number> = {
  PROFILE: 0,
  PP: 0,
  PERSONNEL: 0,
  CERT: 0,
  DOC: 0,
  CAP: 0,
  PRICING: 0
};

/**
 * Generate a unique evidence ID
 */
function generateEvidenceId(type: EvidenceSourceType): string {
  const prefixMap: Record<EvidenceSourceType, string> = {
    profile: 'PROFILE',
    past_performance: 'PP',
    personnel: 'PERSONNEL',
    certification: 'CERT',
    document: 'DOC',
    capability: 'CAP',
    pricing: 'PRICING'
  };

  const prefix = prefixMap[type];
  evidenceCounters[prefix] = (evidenceCounters[prefix] || 0) + 1;
  return `${prefix}-${String(evidenceCounters[prefix]).padStart(3, '0')}`;
}

/**
 * Reset evidence ID counters (useful for testing or new sessions)
 */
export function resetEvidenceCounters(): void {
  for (const key of Object.keys(evidenceCounters)) {
    evidenceCounters[key] = 0;
  }
}

/**
 * Extract keywords from text
 */
function extractKeywords(text: string): string[] {
  if (!text) return [];

  const stopWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
    'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been',
    'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would',
    'could', 'should', 'may', 'might', 'must', 'shall', 'this', 'that',
    'these', 'those', 'it', 'its', 'they', 'them', 'their', 'we', 'us',
    'our', 'you', 'your', 'all', 'any', 'some', 'such', 'no', 'not',
    'services', 'contract', 'project', 'work', 'provide', 'support'
  ]);

  return [...new Set(
    text.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 2 && !stopWords.has(w))
  )];
}

/**
 * Profile Inventory Builder
 * Creates an evidence library from user profile data
 */
export class ProfileInventory {
  private inferencer: CapabilityInferencer;
  private scorer: RelevanceScorer;

  constructor() {
    this.inferencer = new CapabilityInferencer();
    this.scorer = new RelevanceScorer();
  }

  /**
   * Build complete evidence library for a profile
   */
  async buildEvidenceLibrary(profileId: string): Promise<EvidenceLibrary> {
    console.log(`📚 Building evidence library for profile: ${profileId}`);
    resetEvidenceCounters();

    // Load all profile data with individual timing
    console.log('📚 Loading profile data from database...');
    const loadStart = Date.now();

    // Load sequentially with logging for debugging
    console.log('📚 Loading profile...');
    const profile = await this.loadProfile(profileId);
    console.log(`📚 Profile loaded in ${Date.now() - loadStart}ms`);

    console.log('📚 Loading past performance...');
    const ppStart = Date.now();
    const pastPerformance = await this.loadPastPerformance(profileId);
    console.log(`📚 Past performance loaded in ${Date.now() - ppStart}ms (${pastPerformance.length} records)`);

    console.log('📚 Loading key personnel...');
    const kpStart = Date.now();
    const keyPersonnel = await this.loadKeyPersonnel(profileId);
    console.log(`📚 Key personnel loaded in ${Date.now() - kpStart}ms (${keyPersonnel.length} records)`);

    console.log('📚 Loading documents...');
    const docStart = Date.now();
    const documents = await this.loadDocuments(profileId);
    console.log(`📚 Documents loaded in ${Date.now() - docStart}ms (${documents.length} records)`);

    console.log('📚 Loading capabilities...');
    const capStart = Date.now();
    const capabilities = await this.loadCapabilities(profileId);
    console.log(`📚 Capabilities loaded in ${Date.now() - capStart}ms (${capabilities.length} records)`);

    console.log('📚 Loading pricing data...');
    const priceStart = Date.now();
    const pricingData = await this.loadPricingData(profileId);
    console.log(`📚 Pricing data loaded in ${Date.now() - priceStart}ms (${pricingData.length} records)`);

    console.log(`📚 All data loaded in ${Date.now() - loadStart}ms total`);

    // Build evidence items from each source
    const evidenceItems: EvidenceItem[] = [];

    // Profile evidence
    if (profile) {
      evidenceItems.push(...this.buildProfileEvidence(profile));
    }

    // Past performance evidence
    evidenceItems.push(...this.buildPastPerformanceEvidence(pastPerformance));

    // Personnel evidence
    evidenceItems.push(...this.buildPersonnelEvidence(keyPersonnel));

    // Document evidence
    evidenceItems.push(...this.buildDocumentEvidence(documents));

    // Capability evidence
    evidenceItems.push(...this.buildCapabilityEvidence(capabilities));

    // Pricing evidence
    evidenceItems.push(...this.buildPricingEvidence(pricingData));

    // Group by type
    const evidenceByType: Record<EvidenceSourceType, EvidenceItem[]> = {
      profile: [],
      past_performance: [],
      personnel: [],
      certification: [],
      document: [],
      capability: [],
      pricing: []
    };

    for (const item of evidenceItems) {
      evidenceByType[item.sourceType].push(item);
    }

    const library: EvidenceLibrary = {
      profileId,
      generatedAt: new Date().toISOString(),
      totalEvidenceCount: evidenceItems.length,
      evidenceByType,
      allEvidence: evidenceItems
    };

    console.log(`✅ Evidence library built: ${evidenceItems.length} items`);
    console.log(`   - Profile: ${evidenceByType.profile.length}`);
    console.log(`   - Past Performance: ${evidenceByType.past_performance.length}`);
    console.log(`   - Personnel: ${evidenceByType.personnel.length}`);
    console.log(`   - Documents: ${evidenceByType.document.length}`);
    console.log(`   - Capabilities: ${evidenceByType.capability.length}`);
    console.log(`   - Pricing: ${evidenceByType.pricing.length}`);

    return library;
  }

  /**
   * Build evidence library scored for a specific contract
   */
  async buildScoredLibrary(
    profileId: string,
    contract: ContractForMatching
  ): Promise<ContractScoredLibrary> {
    console.log(`📊 Building scored evidence library for contract: ${contract.title}`);

    // Build base library
    const baseLibrary = await this.buildEvidenceLibrary(profileId);

    // Enrich with inferred capabilities
    const enrichedEvidence = inferCapabilitiesFromArray(baseLibrary.allEvidence);

    // Score for contract relevance
    const scoredEvidence = this.scorer.scoreEvidenceArray(enrichedEvidence, contract);

    // Group by tier
    const byTier = groupByTier(scoredEvidence);

    // Calculate unique capabilities
    const allCapabilities = new Set<string>();
    let totalInferred = 0;

    for (const item of scoredEvidence) {
      for (const cap of (item.allCapabilities || [])) {
        allCapabilities.add(cap);
      }
      totalInferred += (item.inferredCapabilities || []).length;
    }

    const scoredLibrary: ContractScoredLibrary = {
      ...baseLibrary,
      contractId: contract.id,
      contractTitle: contract.title,
      scoredEvidence,
      evidenceByTier: byTier,
      summary: {
        highRelevanceCount: byTier.HIGH.length,
        mediumRelevanceCount: byTier.MEDIUM.length,
        lowRelevanceCount: byTier.LOW.length,
        minimalRelevanceCount: byTier.MINIMAL.length,
        totalInferredCapabilities: totalInferred,
        uniqueCapabilities: Array.from(allCapabilities)
      }
    };

    console.log(`✅ Scored library built:`);
    console.log(`   - HIGH relevance: ${byTier.HIGH.length}`);
    console.log(`   - MEDIUM relevance: ${byTier.MEDIUM.length}`);
    console.log(`   - LOW relevance: ${byTier.LOW.length}`);
    console.log(`   - MINIMAL relevance: ${byTier.MINIMAL.length}`);
    console.log(`   - Unique capabilities: ${allCapabilities.size}`);

    return scoredLibrary;
  }

  // ============================================================
  // DATA LOADERS
  // ============================================================

  private async loadProfile(profileId: string): Promise<any> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', profileId)
      .single();

    if (error) {
      console.error('Error loading profile:', error);
      return null;
    }
    return data;
  }

  private async loadPastPerformance(profileId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('past_performance')
      .select('*')
      .eq('profile_id', profileId);

    if (error) {
      console.error('Error loading past performance:', error);
      return [];
    }
    return data || [];
  }

  private async loadKeyPersonnel(profileId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('key_personnel')
      .select('*')
      .eq('profile_id', profileId);

    if (error) {
      console.error('Error loading key personnel:', error);
      return [];
    }
    return data || [];
  }

  private async loadDocuments(profileId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('profile_documents')
      .select('*')
      .eq('profile_id', profileId);

    if (error) {
      console.error('Error loading documents:', error);
      return [];
    }
    return data || [];
  }

  private async loadCapabilities(profileId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('capabilities')
      .select('*')
      .eq('profile_id', profileId);

    if (error) {
      console.error('Error loading capabilities:', error);
      return [];
    }
    return data || [];
  }

  private async loadPricingData(profileId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('enhanced_pricing_data')
      .select('*, pricing_components(*)')
      .eq('profile_id', profileId);

    if (error) {
      console.error('Error loading pricing data:', error);
      return [];
    }
    return data || [];
  }

  // ============================================================
  // EVIDENCE BUILDERS
  // ============================================================

  private buildProfileEvidence(profile: any): EvidenceItem[] {
    if (!profile) return [];

    const items: EvidenceItem[] = [];

    // Company overview evidence
    const overview = [
      profile.company_name,
      profile.value_proposition,
      profile.mission_statement
    ].filter(Boolean).join('. ');

    if (overview) {
      items.push({
        evidenceId: generateEvidenceId('profile'),
        sourceType: 'profile',
        sourceTable: 'profiles',
        sourceRecordId: profile.id,
        contentSummary: `Company overview: ${profile.company_name}`,
        fullContent: overview,
        keywords: extractKeywords(overview),
        confidenceScore: 1.0,
        metadata: {
          companyName: profile.company_name,
          dunsNumber: profile.duns_number,
          cageCode: profile.cage_code
        }
      });
    }

    // Core competencies evidence
    const coreCompetencies = toArray(profile.core_competencies);
    if (coreCompetencies.length > 0) {
      items.push({
        evidenceId: generateEvidenceId('profile'),
        sourceType: 'profile',
        sourceTable: 'profiles',
        sourceRecordId: profile.id,
        contentSummary: `Core competencies: ${coreCompetencies.slice(0, 3).join(', ')}`,
        fullContent: `Core Competencies: ${coreCompetencies.join(', ')}`,
        keywords: coreCompetencies.map((c: string) => c.toLowerCase()),
        confidenceScore: 1.0,
        metadata: { coreCompetencies }
      });
    }

    // Technologies evidence
    const keyTechnologies = toArray(profile.key_technologies);
    if (keyTechnologies.length > 0) {
      items.push({
        evidenceId: generateEvidenceId('profile'),
        sourceType: 'profile',
        sourceTable: 'profiles',
        sourceRecordId: profile.id,
        contentSummary: `Key technologies: ${keyTechnologies.slice(0, 5).join(', ')}`,
        fullContent: `Key Technologies: ${keyTechnologies.join(', ')}`,
        keywords: keyTechnologies.map((t: string) => t.toLowerCase()),
        confidenceScore: 1.0,
        metadata: { keyTechnologies }
      });
    }

    // Certifications evidence
    const certifications = toArray(profile.certifications);
    if (certifications.length > 0) {
      for (const cert of certifications) {
        items.push({
          evidenceId: generateEvidenceId('certification'),
          sourceType: 'certification',
          sourceTable: 'profiles',
          sourceRecordId: profile.id,
          contentSummary: `Certification: ${cert}`,
          fullContent: `Company holds ${cert} certification`,
          keywords: extractKeywords(cert),
          confidenceScore: 1.0,
          metadata: { certification: cert }
        });
      }
    }

    // Set-asides evidence
    const qualifiedSetAsides = toArray(profile.qualified_set_asides);
    if (qualifiedSetAsides.length > 0) {
      items.push({
        evidenceId: generateEvidenceId('profile'),
        sourceType: 'profile',
        sourceTable: 'profiles',
        sourceRecordId: profile.id,
        contentSummary: `Set-aside qualifications: ${qualifiedSetAsides.join(', ')}`,
        fullContent: `Qualified for set-asides: ${qualifiedSetAsides.join(', ')}`,
        keywords: qualifiedSetAsides.map((s: string) => s.toLowerCase()),
        confidenceScore: 1.0,
        metadata: { setAsides: qualifiedSetAsides }
      });
    }

    // Security clearance evidence
    if (profile.security_clearance) {
      items.push({
        evidenceId: generateEvidenceId('profile'),
        sourceType: 'profile',
        sourceTable: 'profiles',
        sourceRecordId: profile.id,
        contentSummary: `Facility clearance: ${profile.security_clearance}`,
        fullContent: `Company holds ${profile.security_clearance} facility clearance`,
        keywords: ['clearance', 'security', profile.security_clearance.toLowerCase()],
        confidenceScore: 1.0,
        metadata: { securityClearance: profile.security_clearance }
      });
    }

    return items;
  }

  private buildPastPerformanceEvidence(pastPerformance: any[]): EvidenceItem[] {
    const items: EvidenceItem[] = [];

    for (const pp of pastPerformance) {
      // Safely convert potentially string fields to arrays
      const deliverables = toArray(pp.deliverables);
      const technologiesUsed = toArray(pp.technologies_used);
      const keyAchievements = toArray(pp.key_achievements);

      const content = [
        `Project: ${pp.project_name || 'Unnamed Project'}`,
        pp.agency ? `Agency: ${pp.agency}` : '',
        pp.contract_value ? `Value: $${pp.contract_value.toLocaleString()}` : '',
        pp.contract_type ? `Type: ${pp.contract_type}` : '',
        pp.project_description ? `Description: ${pp.project_description}` : '',
        deliverables.length ? `Deliverables: ${deliverables.join(', ')}` : '',
        technologiesUsed.length ? `Technologies: ${technologiesUsed.join(', ')}` : '',
        keyAchievements.length ? `Achievements: ${keyAchievements.join('; ')}` : ''
      ].filter(Boolean).join('\n');

      // Calculate duration in months
      let duration: number | undefined;
      if (pp.start_date && pp.end_date) {
        const start = new Date(pp.start_date);
        const end = new Date(pp.end_date);
        duration = Math.round((end.getTime() - start.getTime()) / (30 * 24 * 60 * 60 * 1000));
      }

      items.push({
        evidenceId: generateEvidenceId('past_performance'),
        sourceType: 'past_performance',
        sourceTable: 'past_performance',
        sourceRecordId: pp.id,
        contentSummary: `${pp.project_name || 'Project'} for ${pp.agency || 'federal client'} - $${((pp.contract_value || 0) / 1000000).toFixed(1)}M`,
        fullContent: content,
        keywords: [
          ...extractKeywords(pp.project_name || ''),
          ...extractKeywords(pp.project_description || ''),
          ...technologiesUsed.map((t: string) => t.toLowerCase()),
          ...deliverables.flatMap((d: string) => extractKeywords(d))
        ],
        confidenceScore: 1.0,
        createdAt: pp.start_date,
        metadata: {
          contractValue: pp.contract_value,
          agencyName: pp.agency,
          naicsCode: pp.naics_code,
          setAsides: pp.set_asides,
          duration,
          teamSize: pp.team_size,
          contractNumber: pp.contract_number,
          cparsRating: pp.cpars_rating || pp.performance_rating,
          onTimeDelivery: pp.on_time_delivery,
          withinBudget: pp.within_budget
        }
      });
    }

    return items;
  }

  private buildPersonnelEvidence(personnel: any[]): EvidenceItem[] {
    const items: EvidenceItem[] = [];

    for (const person of personnel) {
      // Safely convert potentially string fields to arrays
      const certifications = toArray(person.certifications);
      const keyAchievements = toArray(person.key_achievements);

      const content = [
        `Name: ${person.name}`,
        person.title ? `Title: ${person.title}` : '',
        person.years_experience ? `Experience: ${person.years_experience} years` : '',
        person.education ? `Education: ${person.education}` : '',
        person.security_clearance ? `Clearance: ${person.security_clearance}` : '',
        certifications.length ? `Certifications: ${certifications.join(', ')}` : '',
        person.role_description ? `Role: ${person.role_description}` : '',
        keyAchievements.length ? `Achievements: ${keyAchievements.join('; ')}` : ''
      ].filter(Boolean).join('\n');

      items.push({
        evidenceId: generateEvidenceId('personnel'),
        sourceType: 'personnel',
        sourceTable: 'key_personnel',
        sourceRecordId: person.id,
        contentSummary: `${person.name} - ${person.title || 'Key Personnel'}`,
        fullContent: content,
        keywords: [
          ...extractKeywords(person.title || ''),
          ...extractKeywords(person.role_description || ''),
          ...certifications.map((c: string) => c.toLowerCase())
        ],
        confidenceScore: 1.0,
        metadata: {
          personName: person.name,
          title: person.title,
          yearsExperience: person.years_experience,
          securityClearance: person.security_clearance,
          certifications: person.certifications,
          education: person.education
        }
      });
    }

    return items;
  }

  private buildDocumentEvidence(documents: any[]): EvidenceItem[] {
    const items: EvidenceItem[] = [];

    for (const doc of documents) {
      // Only include documents with extracted text
      if (!doc.extracted_text || doc.extracted_text.length < 50) continue;

      const summary = doc.extracted_text.substring(0, 200).trim();

      items.push({
        evidenceId: generateEvidenceId('document'),
        sourceType: 'document',
        sourceTable: 'profile_documents',
        sourceRecordId: doc.id,
        contentSummary: `${doc.document_type || 'Document'}: ${doc.file_name}`,
        fullContent: doc.extracted_text,
        keywords: extractKeywords(doc.extracted_text),
        confidenceScore: 0.9, // Slightly lower confidence for extracted text
        createdAt: doc.created_at,
        metadata: {
          documentType: doc.document_type,
          fileName: doc.file_name,
          fileSize: doc.file_size,
          mimeType: doc.mime_type
        }
      });
    }

    return items;
  }

  private buildCapabilityEvidence(capabilities: any[]): EvidenceItem[] {
    const items: EvidenceItem[] = [];

    for (const cap of capabilities) {
      // Safely convert potentially string fields to arrays
      const toolsUsed = toArray(cap.tools_used);
      const differentiators = toArray(cap.differentiators);
      const projectExamples = toArray(cap.project_examples);

      const content = [
        `Capability: ${cap.capability_name}`,
        cap.description ? `Description: ${cap.description}` : '',
        cap.proficiency_level ? `Proficiency: ${cap.proficiency_level}` : '',
        cap.years_experience ? `Experience: ${cap.years_experience} years` : '',
        toolsUsed.length ? `Tools: ${toolsUsed.join(', ')}` : '',
        differentiators.length ? `Differentiators: ${differentiators.join('; ')}` : '',
        projectExamples.length ? `Examples: ${projectExamples.join('; ')}` : ''
      ].filter(Boolean).join('\n');

      items.push({
        evidenceId: generateEvidenceId('capability'),
        sourceType: 'capability',
        sourceTable: 'capabilities',
        sourceRecordId: cap.id,
        contentSummary: `${cap.capability_name} - ${cap.proficiency_level || 'capability'}`,
        fullContent: content,
        keywords: [
          ...extractKeywords(cap.capability_name || ''),
          ...extractKeywords(cap.description || ''),
          ...toolsUsed.map((t: string) => t.toLowerCase())
        ],
        confidenceScore: 1.0,
        metadata: {
          capabilityName: cap.capability_name,
          proficiencyLevel: cap.proficiency_level,
          yearsExperience: cap.years_experience,
          toolsUsed: cap.tools_used,
          geographicCoverage: cap.geographic_coverage
        }
      });
    }

    return items;
  }

  private buildPricingEvidence(pricingData: any[]): EvidenceItem[] {
    const items: EvidenceItem[] = [];

    for (const pricing of pricingData) {
      const pricingComponents = toArray(pricing.pricing_components);
      if (pricingComponents.length === 0) continue;

      const naicsCodes = toArray(pricing.naics_codes);
      const components = pricingComponents.map((c: any) =>
        typeof c === 'object' ? `${c.name}: $${c.unit_cost}/${c.unit || 'hour'}` : String(c)
      ).join(', ');

      items.push({
        evidenceId: generateEvidenceId('pricing'),
        sourceType: 'pricing',
        sourceTable: 'enhanced_pricing_data',
        sourceRecordId: pricing.id,
        contentSummary: `Pricing data for ${pricing.sector || 'services'}`,
        fullContent: `Sector: ${pricing.sector}\nNAICS: ${naicsCodes.join(', ') || 'N/A'}\nComponents: ${components}`,
        keywords: [
          ...extractKeywords(pricing.sector || ''),
          ...naicsCodes
        ],
        confidenceScore: 1.0,
        metadata: {
          sector: pricing.sector,
          naicsCode: pricing.naics_codes?.[0],
          componentCount: pricing.pricing_components.length
        }
      });
    }

    return items;
  }
}

// Export singleton instance
export const profileInventory = new ProfileInventory();

// Export convenience functions
export async function buildEvidenceLibrary(profileId: string): Promise<EvidenceLibrary> {
  return profileInventory.buildEvidenceLibrary(profileId);
}

export async function buildScoredLibrary(
  profileId: string,
  contract: ContractForMatching
): Promise<ContractScoredLibrary> {
  return profileInventory.buildScoredLibrary(profileId, contract);
}
